import React from 'react';
import { PiTrashDuotone } from 'react-icons/pi';
import { Button } from 'rizzui';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import CustomTextDelayInput from '../../../../../components/forms/CustomTextDelayInput';
import DateField from '../../../../../components/forms/DateField';
import useUsers from '../../../../../hooks/api-calls/useUsers';
import { FilterOptions } from '../../../../../hooks/useFilters';

interface VehicleRefuelingFiltersProps {
    updateFilters: (filters: any) => void;
    filters: FilterOptions;
    updateFilterOrder?: (field: string, order: 'asc' | 'desc') => void;
}

const VehicleRefuelingFilters: React.FC<VehicleRefuelingFiltersProps> = ({ filters, updateFilters, updateFilterOrder }) => {

    // HOOKS
    const { getUsersList } = useUsers();
    const users = getUsersList();

    // RENDER
    return (
        <>
            <CustomTextDelayInput
                id="search_text"
                label="Búsqueda"
                placeholder="Buscar por texto..."
                filters={filters}
                updateFilters={updateFilters}
            />

            <CustomSelect
                id="user_ids"
                label="Usuarios"
                placeholder="Seleccionar usuarios"
                options={users || []}
                value={filters.filter_filters?.user_ids ? users?.filter(user => filters.filter_filters?.user_ids.includes(user.value)) || [] : []}
                onChange={(value: any) => updateFilters({ user_ids: value.map((item: any) => item.value) })}
                isClearable={true}
                isMulti={true}
                isLoading={!users}
            />


            <DateField
                isClearable
                className="w-full"
                label="Rango de fechas"
                placeholderText="Selecciona las fechas"
                selectedDateField={filters.filter_filters?.between_dates?.type || "date"}
                dateTypesOptions={[
                    { label: "Fecha de creación", value: "createdAt" },
                    { label: "Fecha de repostaje", value: "date" },
                ]}
                startDate={filters.filter_filters?.between_dates?.startDate ? new Date(filters.filter_filters.between_dates.startDate) : null}
                endDate={filters.filter_filters?.between_dates?.endDate ? new Date(filters.filter_filters.between_dates.endDate) : null}
                onChange={(date: any) => {
                    const filters_between_dates = filters.filter_filters?.between_dates;
                    const between_dates = date ? { startDate: date[0], endDate: date[1] } : null;
                    if (between_dates && filters_between_dates !== between_dates) {
                        updateFilters({ between_dates: { startDate: date[0], endDate: date[1], type: date[2] } });
                    } else if (!between_dates && filters_between_dates) {
                        updateFilters({ between_dates: null });
                    }
                }}
            />

            <div className="grid grid-cols-2 gap-3">
                <CustomTextDelayInput
                    id="minLiters"
                    label="Litros mínimos"
                    placeholder="Ingrese litros mínimos"
                    type="number"
                    step="0.01"
                    min="0"
                    filters={filters}
                    updateFilters={updateFilters}
                    isClearable={true}
                    onClear={() => updateFilters({ minLiters: null })}
                    suffix="L"
                />
                <CustomTextDelayInput
                    id="maxLiters"
                    label="Litros máximos"
                    placeholder="Ingrese litros máximos"
                    type="number"
                    step="0.01"
                    min="0"
                    filters={filters}
                    updateFilters={updateFilters}
                    isClearable={true}
                    onClear={() => updateFilters({ maxLiters: null })}
                    suffix="L"
                />
            </div>

            <div className="grid grid-cols-2 gap-3">
                <CustomTextDelayInput
                    id="minCost"
                    label="Costo mínimo"
                    placeholder="Ingrese costo mínimo"
                    type="number"
                    step="0.01"
                    min="0"
                    filters={filters}
                    updateFilters={updateFilters}
                    isClearable={true}
                    onClear={() => updateFilters({ minCost: null })}
                    suffix="€"
                />
                <CustomTextDelayInput
                    id="maxCost"
                    label="Costo máximo"
                    placeholder="Ingrese costo máximo"
                    type="number"
                    step="0.01"
                    min="0"
                    filters={filters}
                    updateFilters={updateFilters}
                    isClearable={true}
                    onClear={() => updateFilters({ maxCost: null })}
                    suffix="€"
                />
            </div>

            <CustomSelect
                id="sort"
                label="Ordenar por"
                placeholder="Seleccionar orden"
                options={[
                    { value: 'date_desc', label: 'Fecha (más reciente)' },
                    { value: 'date_asc', label: 'Fecha (más antigua)' },
                    { value: 'totalCost_desc', label: 'Costo (mayor a menor)' },
                    { value: 'totalCost_asc', label: 'Costo (menor a mayor)' },
                    { value: 'liters_desc', label: 'Litros (mayor a menor)' },
                    { value: 'liters_asc', label: 'Litros (menor a mayor)' },
                    { value: 'createdAt_desc', label: 'Creación (más reciente)' },
                    { value: 'createdAt_asc', label: 'Creación (más antigua)' },
                ]}
                value={filters.filter_order && filters.filter_order.length > 0 ?
                    {
                        value: `${filters.filter_order[0].field}_${filters.filter_order[0].order}`,
                        label: [
                            { value: 'date_desc', label: 'Fecha (más reciente)' },
                            { value: 'date_asc', label: 'Fecha (más antigua)' },
                            { value: 'totalCost_desc', label: 'Costo (mayor a menor)' },
                            { value: 'totalCost_asc', label: 'Costo (menor a mayor)' },
                            { value: 'liters_desc', label: 'Litros (mayor a menor)' },
                            { value: 'liters_asc', label: 'Litros (menor a mayor)' },
                            { value: 'createdAt_desc', label: 'Creación (más reciente)' },
                            { value: 'createdAt_asc', label: 'Creación (más antigua)' },
                        ].find(option => filters.filter_order && option.value === `${filters.filter_order[0].field}_${filters.filter_order[0].order}`)?.label
                    } : null
                }
                onChange={(value: any) => {
                    if (value && updateFilterOrder) {
                        const [field, order] = value.value.split('_');
                        updateFilterOrder(field, order as 'asc' | 'desc');
                    } else if (!value && updateFilterOrder) {
                        updateFilterOrder("", "asc");
                    }
                }}
                isClearable={true}
            />


        </>
    );
};

export default VehicleRefuelingFilters;